/* eslint-disable */
/**
 * quality.js — image-quality measurements on a face crop, identical in the browser and in Node tests.
 * Input is always a small RGBA crop of the face (the kiosk draws the detected face box into a 96x96
 * canvas), so the numbers do not depend on the camera resolution.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.FaceQuality = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  const SIZE = 96;

  /** RGBA bytes -> luma (0..255) */
  function gray(rgba, w, h) {
    const out = new Float32Array(w * h);
    for (let i = 0, j = 0; i < out.length; i++, j += 4) {
      out[i] = 0.299 * rgba[j] + 0.587 * rgba[j + 1] + 0.114 * rgba[j + 2];
    }
    return out;
  }

  /** Variance of the 4-neighbour Laplacian: high = sharp detail, low = blurry / motion smear. */
  function sharpness(g, w, h) {
    let sum = 0, sumSq = 0, n = 0;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const i = y * w + x;
        const lap = 4 * g[i] - g[i - 1] - g[i + 1] - g[i - w] - g[i + w];
        sum += lap; sumSq += lap * lap; n++;
      }
    }
    const mean = sum / n;
    return sumSq / n - mean * mean;
  }

  /** Mean luma of the central part of the crop (skips hair / background at the edges). */
  function brightness(g, w, h) {
    const x0 = Math.floor(w * 0.2), x1 = Math.ceil(w * 0.8), y0 = Math.floor(h * 0.2), y1 = Math.ceil(h * 0.8);
    let s = 0, n = 0;
    for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) { s += g[y * w + x]; n++; }
    return s / n;
  }

  /** { sharp, bright } from an RGBA crop. */
  function measure(rgba, w = SIZE, h = SIZE) {
    const g = gray(rgba, w, h);
    return { sharp: sharpness(g, w, h), bright: brightness(g, w, h) };
  }

  /**
   * Area-average ("box filter") resize of an RGBA region to size x size. Done in plain JS on purpose, so the
   * browser and the Node tests produce the same numbers (canvas down-scaling differs between browsers).
   */
  function areaResizeRGBA(src, sw, sh, size = SIZE) {
    const out = new Uint8ClampedArray(size * size * 4);
    if (sw < 2 || sh < 2) return out;
    for (let y = 0; y < size; y++) {
      const ya = (y / size) * sh, yb = ((y + 1) / size) * sh;
      const y0 = Math.floor(ya), y1 = Math.max(y0 + 1, Math.ceil(yb));
      for (let x = 0; x < size; x++) {
        const xa = (x / size) * sw, xb = ((x + 1) / size) * sw;
        const x0 = Math.floor(xa), x1 = Math.max(x0 + 1, Math.ceil(xb));
        let r = 0, g = 0, b = 0, n = 0;
        for (let yy = y0; yy < y1 && yy < sh; yy++) {
          for (let xx = x0; xx < x1 && xx < sw; xx++) {
            const i = (yy * sw + xx) * 4;
            r += src[i]; g += src[i + 1]; b += src[i + 2]; n++;
          }
        }
        const o = (y * size + x) * 4;
        out[o] = r / n; out[o + 1] = g / n; out[o + 2] = b / n; out[o + 3] = 255;
      }
    }
    return out;
  }

  /** Measure a face region of arbitrary size: { sharp, bright }. */
  function measureRegion(rgba, w, h) {
    return measure(areaResizeRGBA(rgba, w, h, SIZE), SIZE, SIZE);
  }

  return { SIZE, gray, sharpness, brightness, measure, areaResizeRGBA, measureRegion };
});
